import {
  AIAskModule,
  ClipboardModule,
  ImportantDatesModule,
  LeaderboardModule,
  MarketModule,
  MissionBoardModule,
  NotesModule,
  PinnedMissionsModule,
  PomodoroModule,
  QuickLinksModule,
  RoutinesModule,
  TodosModule,
  WorkoutTimerModule,
} from '../components/dashboard/DashboardModules';
import {
  AppModuleDefinition,
  AppModuleId,
  DashboardBoardLayout,
  DashboardModuleColor,
  DashboardModuleDefaultSize,
  DashboardGridLayoutItem,
  DashboardModuleSpan,
  DashboardPlacement,
  SidebarLayout,
} from '../types';
import { BOARD_LAYOUT_ROOT_COLUMN_COUNT } from './dashboardBoardLayout';

export const DEFAULT_DASHBOARD_ORDER: AppModuleId[] = [
  'missions-board',
  'quicklinks',
  'leaderboard',
  'todos',
  'notes',
  'routines',
  'market',
];

const DEFAULT_GRID_HEIGHT_BY_SIZE: Record<DashboardModuleDefaultSize, number> = {
  sm: 12,
  md: 14,
  lg: 16,
};

const MODULE_CONTRACT_FIELDS = ['id', 'title', 'defaultSize', 'component', 'storageKey', 'status', 'tags'] as const;

const LEGACY_SIDEBAR_TO_MODULE: Partial<Record<string, AppModuleId>> = {
  quicklinks: 'quicklinks',
  leaderboard: 'leaderboard',
  todos: 'todos',
  notes: 'notes',
  routines: 'routines',
  ticker: 'market',
  dates: 'important-dates',
  clipboard: 'clipboard',
  pinned: 'pinned-missions',
};

export const appModuleRegistry: AppModuleDefinition[] = [
  {
    id: 'missions-board',
    title: 'Missions Board',
    description: '미션 진행률과 전체 미션 목록을 관리하는 메인 보드 탭입니다.',
    icon: 'MS',
    singleton: true,
    defaultSize: 'lg',
    component: MissionBoardModule,
    storageKey: 'crypto-missions:modules:missions-board',
    status: 'active',
    tags: ['missions', 'tasks', 'board'],
  },
  {
    id: 'ai-ask',
    title: 'AI Ask',
    description: '파이어베이스 함수를 통해 AI에게 질문하고 답을 받는 채팅 탭입니다.',
    icon: 'AI',
    singleton: true,
    defaultSize: 'lg',
    component: AIAskModule,
    storageKey: 'crypto-missions:modules:ai-ask',
    status: 'active',
    tags: ['ai', 'assistant', 'chat'],
  },
  {
    id: 'quicklinks',
    title: 'Quick Links',
    description: '자주 여는 사이트와 작업 링크를 빠르게 모아두는 탭입니다.',
    icon: 'QL',
    singleton: true,
    defaultSize: 'md',
    component: QuickLinksModule,
    storageKey: 'crypto-missions:quickLinks',
    status: 'active',
    tags: ['links', 'bookmarks', 'reference'],
  },
  {
    id: 'market',
    title: 'Market',
    description: '코인 시세와 트렌딩 정보를 한눈에 보는 마켓 탭입니다.',
    icon: 'MK',
    singleton: true,
    defaultSize: 'md',
    component: MarketModule,
    storageKey: 'crypto-missions:modules:market',
    status: 'active',
    tags: ['crypto', 'prices', 'coingecko'],
  },
  {
    id: 'leaderboard',
    title: 'Leaderboard',
    description: '랭킹, 캠페인, 포인트 보드 링크를 모아보는 탭입니다.',
    icon: 'LB',
    singleton: true,
    defaultSize: 'md',
    component: LeaderboardModule,
    storageKey: 'crypto-missions:leaderboards',
    status: 'active',
    tags: ['ranking', 'campaigns', 'links'],
  },
  {
    id: 'todos',
    title: 'To-Do List',
    description: '오늘 해야 할 일을 체크리스트로 관리하는 탭입니다.',
    icon: 'TD',
    singleton: true,
    defaultSize: 'md',
    component: TodosModule,
    storageKey: 'crypto-missions:todos',
    status: 'active',
    tags: ['tasks', 'checklist', 'planning'],
  },
  {
    id: 'notes',
    title: 'Notes',
    description: '자유롭게 메모하고 편집기 높이도 저장되는 노트 탭입니다.',
    icon: 'NT',
    singleton: true,
    defaultSize: 'md',
    component: NotesModule,
    storageKey: 'crypto-missions:notes',
    status: 'active',
    tags: ['writing', 'memo', 'journal'],
  },
  {
    id: 'routines',
    title: 'Routine',
    description: '반복 습관과 루틴을 체크리스트로 관리하는 탭입니다.',
    icon: 'RT',
    singleton: true,
    defaultSize: 'md',
    component: RoutinesModule,
    storageKey: 'crypto-missions:routines',
    status: 'active',
    tags: ['habits', 'daily', 'checklist'],
  },
  {
    id: 'pomodoro',
    title: 'Pomodoro',
    description: '집중 시간을 재는 미니 포모도로 타이머 탭입니다.',
    icon: 'PM',
    singleton: true,
    defaultSize: 'sm',
    component: PomodoroModule,
    storageKey: 'crypto-missions:modules:pomodoro',
    status: 'active',
    tags: ['timer', 'focus', 'productivity'],
  },
  {
    id: 'workout-timer',
    title: 'Workout Timer',
    description: '운동 시간을 간단히 재고 기록하는 타이머 탭입니다.',
    icon: 'WT',
    singleton: true,
    defaultSize: 'sm',
    component: WorkoutTimerModule,
    storageKey: 'crypto-missions:modules:workout-timer',
    status: 'active',
    tags: ['timer', 'workout', 'fitness'],
  },
  {
    id: 'important-dates',
    title: 'Important Dates',
    description: '나중에 다시 올릴 수 있도록 보관 중인 일정 탭입니다.',
    icon: 'DT',
    singleton: true,
    defaultSize: 'md',
    component: ImportantDatesModule,
    storageKey: 'crypto-missions:importantDates',
    status: 'backlog',
    tags: ['calendar', 'dates', 'events'],
  },
  {
    id: 'clipboard',
    title: 'Clipboard',
    description: '기본 목록 밖에서 보관 중인 복사 보조 탭입니다.',
    icon: 'CB',
    singleton: true,
    defaultSize: 'sm',
    component: ClipboardModule,
    storageKey: 'crypto-missions:modules:clipboard',
    status: 'legacy',
    tags: ['copy', 'tags', 'utility'],
  },
  {
    id: 'pinned-missions',
    title: 'Pinned Missions',
    description: '나중에 다시 활용할 수 있도록 남겨둔 미션 바로가기 탭입니다.',
    icon: 'PN',
    singleton: true,
    defaultSize: 'md',
    component: PinnedMissionsModule,
    storageKey: 'crypto-missions:modules:pinned-missions',
    status: 'legacy',
    tags: ['missions', 'yaping', 'shortcuts'],
  },
];

export function getSpanFromDefaultSize(defaultSize: DashboardModuleDefaultSize): DashboardModuleSpan {
  switch (defaultSize) {
    case 'sm':
      return 1;
    case 'lg':
      return 4;
    case 'md':
    default:
      return 2;
  }
}

export function getAppModuleDefinition(moduleId: AppModuleId) {
  const definition = appModuleRegistry.find((module) => module.id === moduleId);
  if (!definition) {
    return null;
  }

  validateModuleContract(definition);
  return definition;
}

export function validateModuleContract(definition: AppModuleDefinition) {
  for (const field of MODULE_CONTRACT_FIELDS) {
    const value = definition[field];
    if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) {
      throw new Error(`Invalid module contract for "${definition.id}": missing ${field}`);
    }
  }

  if (!definition.storageKey.startsWith('crypto-missions:')) {
    throw new Error(`Invalid module contract for "${definition.id}": storageKey must start with "crypto-missions:"`);
  }
}

function createPlacement(
  moduleId: AppModuleId,
  order: number,
  moduleColor: DashboardModuleColor = 'default',
  span?: DashboardModuleSpan
): DashboardPlacement {
  const definition = getAppModuleDefinition(moduleId);
  if (!definition) {
    throw new Error(`Cannot create dashboard placement for unknown module "${moduleId}".`);
  }

  return {
    placementId: moduleId,
    moduleId,
    order,
    visible: true,
    moduleColor,
    span: span ?? getSpanFromDefaultSize(definition.defaultSize),
  };
}

function cloneBoardLayout(layout: DashboardBoardLayout): DashboardBoardLayout {
  return {
    order: Array.isArray(layout.order) ? [...layout.order] : [],
    gridLayouts: {
      xl: Array.isArray(layout.gridLayouts?.xl) ? layout.gridLayouts.xl.map((item) => ({ ...item })) : undefined,
    },
    placements: Object.fromEntries(
      Object.entries(layout.placements ?? {}).map(([placementId, placement]) => [placementId, { ...placement }])
    ),
  };
}

function clampGridNumber(value: number, minimum: number, maximum: number) {
  if (!Number.isFinite(value)) {
    return minimum;
  }

  return Math.max(minimum, Math.min(Math.round(value), maximum));
}

function sortGridLayoutItems(items: DashboardGridLayoutItem[]) {
  return [...items].sort((left, right) =>
    left.y - right.y ||
    left.x - right.x ||
    left.i.localeCompare(right.i)
  );
}

function getDefaultGridHeight(moduleId: AppModuleId) {
  const definition = getAppModuleDefinition(moduleId);
  return DEFAULT_GRID_HEIGHT_BY_SIZE[definition?.defaultSize ?? 'md'];
}

function getLegacyOrder(
  baseLayout: DashboardBoardLayout,
  storedLayout?: DashboardBoardLayout | null
) {
  const oldColumns = (storedLayout as DashboardBoardLayout & { columns?: Record<string, string[]> } | null)?.columns;
  const legacyOrderFromColumns = (['column1', 'column2', 'column3', 'column4'] as const).flatMap(
    (columnId) => (Array.isArray(oldColumns) ? [] : oldColumns?.[columnId] ?? [])
  );
  const legacyOrderFromDesktopColumns = (storedLayout?.desktopColumnsV2 ?? []).flatMap((column) => column.itemIds ?? []);

  if (Array.isArray(baseLayout.order) && baseLayout.order.length > 0) {
    return [...baseLayout.order];
  }

  if (legacyOrderFromDesktopColumns.length > 0) {
    return legacyOrderFromDesktopColumns;
  }

  return legacyOrderFromColumns;
}

function hasStoredLayoutContent(storedLayout?: DashboardBoardLayout | null) {
  if (!storedLayout || typeof storedLayout !== 'object' || Array.isArray(storedLayout)) {
    return false;
  }

  const legacyColumns = (storedLayout as DashboardBoardLayout & { columns?: Record<string, string[]> }).columns;
  const hasLegacyColumns = Boolean(
    legacyColumns &&
    Object.values(legacyColumns).some((column) => Array.isArray(column) && column.length > 0)
  );
  const hasDesktopColumns = Boolean(
    storedLayout.desktopColumnsV2?.some((column) => Array.isArray(column.itemIds) && column.itemIds.length > 0)
  );

  return Boolean(
    (Array.isArray(storedLayout.order) && storedLayout.order.length > 0) ||
    (storedLayout.placements && Object.keys(storedLayout.placements).length > 0) ||
    hasLegacyColumns ||
    hasDesktopColumns
  );
}

function normalizeGridLayoutItem(
  item: DashboardGridLayoutItem,
  columnCount: number
) {
  const w = clampGridNumber(item.w, 1, columnCount);

  return {
    i: item.i,
    x: clampGridNumber(item.x, 0, Math.max(columnCount - w, 0)),
    y: clampGridNumber(item.y, 0, Number.MAX_SAFE_INTEGER),
    w,
    h: clampGridNumber(item.h, 1, Number.MAX_SAFE_INTEGER),
  };
}

function canPlaceGridItem(
  existingItems: DashboardGridLayoutItem[],
  x: number,
  y: number,
  w: number,
  h: number
) {
  return !existingItems.some((item) =>
    x < item.x + item.w &&
    x + w > item.x &&
    y < item.y + item.h &&
    y + h > item.y
  );
}

function findFirstGridSlot(
  existingItems: DashboardGridLayoutItem[],
  w: number,
  h: number,
  columnCount: number
) {
  let y = 0;

  while (y < 1000) {
    for (let x = 0; x <= columnCount - w; x += 1) {
      if (canPlaceGridItem(existingItems, x, y, w, h)) {
        return { x, y };
      }
    }

    y += 1;
  }

  const bottom = existingItems.reduce((maxBottom, item) => Math.max(maxBottom, item.y + item.h), 0);
  return { x: 0, y: bottom };
}

function createGridItemFromPlacement(
  placementId: string,
  placements: Record<string, DashboardPlacement>,
  existingItems: DashboardGridLayoutItem[],
  columnCount: number
) {
  const placement = placements[placementId];
  const w = clampGridNumber(placement?.span ?? 2, 1, columnCount);
  const h = getDefaultGridHeight(placement?.moduleId ?? 'todos');
  const position = findFirstGridSlot(existingItems, w, h, columnCount);

  return {
    i: placementId,
    x: position.x,
    y: position.y,
    w,
    h,
  };
}

function buildGridLayoutFromVisibleOrder(
  visibleOrder: string[],
  placements: Record<string, DashboardPlacement>,
  columnCount = BOARD_LAYOUT_ROOT_COLUMN_COUNT
) {
  const items: DashboardGridLayoutItem[] = [];

  visibleOrder.forEach((placementId) => {
    if (!placements[placementId]?.visible) {
      return;
    }

    items.push(createGridItemFromPlacement(placementId, placements, items, columnCount));
  });

  return sortGridLayoutItems(items);
}

function normalizeGridLayoutXl(
  baseLayout: DashboardBoardLayout,
  visibleOrder: string[],
  placements: Record<string, DashboardPlacement>,
  columnCount = BOARD_LAYOUT_ROOT_COLUMN_COUNT
) {
  const visibleSet = new Set(visibleOrder);
  const existingItems = Array.isArray(baseLayout.gridLayouts?.xl) ? baseLayout.gridLayouts.xl : [];
  const nextItems: DashboardGridLayoutItem[] = [];
  const seen = new Set<string>();

  sortGridLayoutItems(existingItems).forEach((item) => {
    if (!visibleSet.has(item.i) || seen.has(item.i) || !placements[item.i]?.visible) {
      return;
    }

    const normalizedItem = normalizeGridLayoutItem(item, columnCount);
    if (canPlaceGridItem(nextItems, normalizedItem.x, normalizedItem.y, normalizedItem.w, normalizedItem.h)) {
      nextItems.push(normalizedItem);
      seen.add(item.i);
      return;
    }

    nextItems.push(createGridItemFromPlacement(item.i, placements, nextItems, columnCount));
    seen.add(item.i);
  });

  visibleOrder.forEach((placementId) => {
    if (seen.has(placementId) || !placements[placementId]?.visible) {
      return;
    }

    nextItems.push(createGridItemFromPlacement(placementId, placements, nextItems, columnCount));
  });

  return sortGridLayoutItems(nextItems);
}

export function serializeDashboardGridLayoutItems(
  items: ReadonlyArray<Pick<DashboardGridLayoutItem, 'i' | 'x' | 'y' | 'w' | 'h'>>,
  columnCount = BOARD_LAYOUT_ROOT_COLUMN_COUNT
) {
  return sortGridLayoutItems(
    items.map((item) =>
      normalizeGridLayoutItem(
        {
          i: item.i,
          x: item.x,
          y: item.y,
          w: item.w,
          h: item.h,
        },
        columnCount
      )
    )
  );
}

function ensureMissionBoardPlacement(
  placements: Record<string, DashboardPlacement>,
  registryMap: Record<string, AppModuleDefinition>
) {
  if (!placements['missions-board'] && registryMap['missions-board']) {
    placements['missions-board'] = createPlacement('missions-board', 0);
  }

  placements['missions-board'] = {
    ...placements['missions-board'],
    visible: true,
  };
}

export function buildDefaultDashboardBoardLayout(): DashboardBoardLayout {
  const layout = createDefaultDashboardBoardLayoutSeed();
  return normalizeDashboardBoardLayout(layout);
}

function createDefaultDashboardBoardLayoutSeed(): DashboardBoardLayout {
  const layout: DashboardBoardLayout = {
    order: [],
    placements: {},
    gridLayouts: {},
  };

  DEFAULT_DASHBOARD_ORDER.forEach((moduleId, index) => {
    layout.order.push(moduleId);
    layout.placements[moduleId] = createPlacement(moduleId, index);
  });

  return layout;
}

export function buildDashboardBoardFromLegacy(legacyLayout?: SidebarLayout | null): DashboardBoardLayout {
  if (!legacyLayout) {
    return createDefaultDashboardBoardLayoutSeed();
  }

  const nextLayout: DashboardBoardLayout = {
    order: [],
    placements: {},
    gridLayouts: {},
  };

  (['column1', 'column2', 'column3', 'column4'] as const).forEach((columnId) => {
    const mappedIds = (legacyLayout[columnId] ?? [])
      .map((itemId) => LEGACY_SIDEBAR_TO_MODULE[itemId])
      .filter((moduleId): moduleId is AppModuleId =>
        Boolean(moduleId && ['quicklinks', 'market', 'leaderboard', 'todos', 'notes', 'routines'].includes(moduleId))
      );

    mappedIds.forEach((moduleId) => {
      if (nextLayout.placements[moduleId]) return;

      nextLayout.order.push(moduleId);
      nextLayout.placements[moduleId] = createPlacement(moduleId, nextLayout.order.length - 1);
    });
  });

  return nextLayout.order.length > 0 ? nextLayout : createDefaultDashboardBoardLayoutSeed();
}

export function normalizeDashboardBoardLayout(
  storedLayout?: DashboardBoardLayout | null,
  legacyLayout?: SidebarLayout | null
): DashboardBoardLayout {
  const hasUsableStoredLayout = hasStoredLayoutContent(storedLayout);
  const fallbackLayout = buildDashboardBoardFromLegacy(legacyLayout);
  const candidateLayout = hasUsableStoredLayout ? cloneBoardLayout(storedLayout) : fallbackLayout;
  const candidateOrder = getLegacyOrder(candidateLayout, storedLayout);
  const baseLayout =
    candidateOrder.length === 0 && Object.keys(candidateLayout.placements ?? {}).length === 0
      ? fallbackLayout
      : candidateLayout;
  const registryMap = Object.fromEntries(
    appModuleRegistry.map((module) => {
      validateModuleContract(module);
      return [module.id, module];
    })
  );
  const legacyOrder = getLegacyOrder(baseLayout, storedLayout);

  const normalized: DashboardBoardLayout = {
    order: [],
    placements: {},
    gridLayouts: {},
  };
  const seen = new Set<string>();
  const visibleOrder: string[] = [];

  const applyPlacement = (placementId: string, placement: DashboardPlacement | undefined) => {
    if (!placement || seen.has(placementId) || !registryMap[placement.moduleId]) {
      return;
    }

    const normalizedPlacement: DashboardPlacement = {
      placementId,
      moduleId: placement.moduleId,
      order: placement.order ?? -1,
      columnOrder: placement.columnOrder,
      visible: placement.visible !== false,
      moduleColor: placement.moduleColor ?? 'default',
      span: clampGridNumber(
        placement.span ?? getSpanFromDefaultSize(registryMap[placement.moduleId].defaultSize),
        1,
        BOARD_LAYOUT_ROOT_COLUMN_COUNT
      ) as DashboardModuleSpan,
      columnId: placement.columnId,
    };

    normalized.placements[placementId] = normalizedPlacement;
    seen.add(placementId);

    if (normalizedPlacement.visible) {
      visibleOrder.push(placementId);
    }
  };

  legacyOrder.forEach((placementId) => applyPlacement(placementId, baseLayout.placements[placementId]));

  Object.values(baseLayout.placements ?? {})
    .sort((left, right) =>
      (left.order ?? Number.MAX_SAFE_INTEGER) - (right.order ?? Number.MAX_SAFE_INTEGER) ||
      left.placementId.localeCompare(right.placementId)
    )
    .forEach((placement) => applyPlacement(placement.placementId, placement));

  ensureMissionBoardPlacement(normalized.placements, registryMap);

  const visibleWithoutMission = visibleOrder.filter((placementId) => placementId !== 'missions-board');
  const finalVisibleOrder = ['missions-board', ...visibleWithoutMission];
  const gridLayoutXl = normalizeGridLayoutXl(
    baseLayout,
    finalVisibleOrder,
    normalized.placements
  );
  const visibleGridIds = new Set(gridLayoutXl.map((item) => item.i));
  const nextOrder = finalVisibleOrder.filter((placementId) => visibleGridIds.has(placementId));
  const visibleSet = new Set(nextOrder);

  normalized.gridLayouts = {
    xl: gridLayoutXl,
  };
  normalized.order = nextOrder;

  nextOrder.forEach((placementId, index) => {
    const placement = normalized.placements[placementId];
    if (!placement) {
      return;
    }

    normalized.placements[placementId] = {
      ...placement,
      visible: true,
      order: index,
    };
  });

  Object.values(normalized.placements).forEach((placement) => {
    if (visibleSet.has(placement.placementId)) {
      return;
    }

    normalized.placements[placement.placementId] = {
      ...placement,
      visible: false,
      order: -1,
    };
  });

  return normalized;
}
